import React from "react";
import { Link } from "react-router-dom";
import { Store, Mail, FileText, Shield, Scale, HelpCircle } from "lucide-react";

const XLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LineLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
);

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  faq_url: string | null;
  contact_url: string | null;
  show_terms?: boolean;
  show_privacy?: boolean;
  show_trade_law?: boolean;
  show_faq?: boolean;
  show_contact?: boolean;
  show_powered_by?: boolean;
  x_account?: string;
  line_url?: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
  theme_config?: {
    logo_type?: 'text' | 'image';
    logo_text?: string | null;
    logo_image_url?: string | null;
  };
}

interface FooterProps {
  siteData?: Site;
}

const footerLinkClass = "inline-flex items-center gap-1.5 px-3 py-2 bg-white border-theme rounded-theme text-xs [font-weight:var(--theme-font-weight-button)] hover:bg-theme-primary/20 transition-colors";

const Footer: React.FC<FooterProps> = ({ siteData }) => {
  return (
    <footer className="bg-theme-bg border-t-[length:var(--theme-border-width)] border-t-[color:var(--theme-border-color)] mt-12">
      <div className="container mx-auto max-w-screen-lg px-4 py-8">
        {/* メインコンテンツ */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          {/* 店舗情報 */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              {siteData?.theme_config?.logo_type === "image" && siteData?.theme_config?.logo_image_url ? (
                <img
                  src={siteData.theme_config.logo_image_url}
                  alt={siteData?.name || "JOYBASE"}
                  className="h-8 max-w-[160px] object-contain"
                />
              ) : (
                <>
                  <div className="w-8 h-8 bg-theme-primary border-theme rounded-theme flex items-center justify-center">
                    <Store className="w-4 h-4 text-theme-button" />
                  </div>
                  <h3 className="[font-weight:var(--theme-font-weight)] text-lg text-theme-bg-text">
                    {siteData?.theme_config?.logo_text || siteData?.name || 'JOYBASE'}
                  </h3>
                </>
              )}
            </div>
            <p className="text-sm text-theme-bg-text [font-weight:var(--theme-font-weight-button)] max-w-sm">
              {siteData?.description || 'カードショッププラットフォーム'}
            </p>
            {(siteData?.x_account || siteData?.line_url) && (
              <div className="mt-4 flex gap-3">
                {siteData?.x_account && (
                  <a
                    href={`https://x.com/${siteData.x_account.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-theme-bg-text text-theme-bg border-theme rounded-theme flex items-center justify-center hover:bg-theme-primary hover:text-theme-button transition-colors"
                  >
                    <XLogo className="w-5 h-5" />
                  </a>
                )}
                {siteData?.line_url && (
                  <a
                    href={siteData.line_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LINE"
                    className="w-10 h-10 bg-theme-bg-text text-theme-bg border-theme rounded-theme flex items-center justify-center hover:bg-theme-primary hover:text-theme-button transition-colors"
                  >
                    <LineLogo className="w-6 h-6" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* リンク */}
          <div className="flex flex-wrap gap-3">
            {/* 利用規約 */}
            {siteData?.show_terms !== false && (
              siteData?.terms_url && siteData.terms_url.startsWith('http') ? (
                <a
                  href={siteData.terms_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClass}
                >
                  <FileText className="w-3.5 h-3.5" />
                  利用規約
                </a>
              ) : (
                <Link to="/terms" className={footerLinkClass}>
                  <FileText className="w-3.5 h-3.5" />
                  利用規約
                </Link>
              )
            )}

            {/* プライバシーポリシー */}
            {siteData?.show_privacy !== false && (
              siteData?.privacy_url && siteData.privacy_url.startsWith('http') ? (
                <a
                  href={siteData.privacy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClass}
                >
                  <Shield className="w-3.5 h-3.5" />
                  プライバシーポリシー
                </a>
              ) : (
                <Link to="/privacy" className={footerLinkClass}>
                  <Shield className="w-3.5 h-3.5" />
                  プライバシーポリシー
                </Link>
              )
            )}

            {/* 特定商取引法に基づく表記 */}
            {siteData?.show_trade_law !== false && (
              siteData?.trade_law_url && siteData.trade_law_url.startsWith('http') ? (
                <a
                  href={siteData.trade_law_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClass}
                >
                  <Scale className="w-3.5 h-3.5" />
                  特定商取引法に基づく表記
                </a>
              ) : (
                <Link to="/trade-law" className={footerLinkClass}>
                  <Scale className="w-3.5 h-3.5" />
                  特定商取引法に基づく表記
                </Link>
              )
            )}

            {/* FAQ（show_faq=trueかつ有効なURLが設定されている場合のみ表示） */}
            {siteData?.show_faq !== false && siteData?.faq_url && siteData.faq_url.startsWith('http') && (
              <a
                href={siteData.faq_url}
                target="_blank"
                rel="noopener noreferrer"
                className={footerLinkClass}
              >
                <HelpCircle className="w-3.5 h-3.5" />
                よくある質問
              </a>
            )}

            {/* お問い合わせ */}
            {siteData?.show_contact !== false && (
              siteData?.contact_url && siteData.contact_url.startsWith('http') ? (
                <a
                  href={siteData.contact_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={footerLinkClass}
                >
                  <Mail className="w-3.5 h-3.5" />
                  お問い合わせ
                </a>
              ) : (
                <Link to="/contact" className={footerLinkClass}>
                  <Mail className="w-3.5 h-3.5" />
                  お問い合わせ
                </Link>
              )
            )}
          </div>
        </div>

        {/* 著作権 */}
        <div className="mt-8 pt-6 border-t-[length:var(--theme-border-width)] border-t-[color:var(--theme-border-color)]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
              &copy; {new Date().getFullYear()} {siteData?.name || 'JOYBASE'}. All rights reserved.
            </p>
            {siteData?.show_powered_by !== false && (
              <a
                href="https://joybase.jp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-60 hover:opacity-100 transition-colors"
              >
                Powered by
                <span className="text-theme-bg-text">JOYBASE</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
